import { Button } from "@components/button";
import { Input } from "@components/input";
import { Modal } from "@components/modal";
import { DefaultValues, Path, PathValue, useForm } from "react-hook-form";
import styles from "./modal.module.scss";
import { CustomSelect, SelectOption } from "@components/select";
import { Calendar } from "@assets/components/ui/calendar";
import { useCallback, useEffect, useState } from "react";
import { DateRange } from "react-day-picker";
import { Grid, GridCol } from "@components/grid";

type SumFilter<T extends Record<string, unknown>> = {
  type: "sum";
  minName: Path<T>;
  maxName: Path<T>;
  defaultValue?: number;
};

type SelectFilter<T extends Record<string, unknown>> = {
  type: "select";
  options: SelectOption[];
  label?: string;
  defaultValue?: string;
  placeholder?: string;
  name: Path<T>;
};

type DateFilter<T extends Record<string, unknown>> = {
  type: "date";
  withTime?: boolean;
  defaultValue?: string;
  nameStart: Path<T>;
  nameEnd: Path<T>;
  returnValue: "string" | "number";
};

export type FilterConfig<T extends Record<string, unknown>> = (
  | DateFilter<T>
  | SelectFilter<T>
  | SumFilter<T>
)[];

// Validation rule for form-level checks
export type ValidationRule<T extends Record<string, unknown>> = {
  validate: (data: T) => boolean;
  message: string;
};

export type FilterProps<T extends Record<string, unknown>> = {
  filterConfig: FilterConfig<T>;
  validationRules?: ValidationRule<T>[]; // Optional array of validation rules
  onSubmit: (data: T) => void; // Callback to handle form submission
  defaultValues?: DefaultValues<T>;
};

// Updated props interface
type Props<T extends Record<string, unknown>> = {
  isModalOpen: boolean;
  hideModal: () => void;
} & FilterProps<T>;

export type TimeProps = {
  hoursStart: string;
  minutesStart: string;
  hoursEnd: string;
  minutesEnd: string;
};

export const FilterModal = <T extends Record<string, unknown>>({
  hideModal,
  isModalOpen,
  filterConfig,
  validationRules = [],
  onSubmit,
  defaultValues,
}: Props<T>) => {
  const { control, handleSubmit, reset, setValue, resetField } = useForm<T>({
    defaultValues,
  });

  const {
    control: timeControl,
    watch: timeWatch,
    getValues: getTimeValues,
  } = useForm<TimeProps>();

  const [range, setRange] = useState<DateRange | undefined>(); // State for calendar range
  const [errors, setErrors] = useState<string[]>([]); // Validation error messages

  // Handle form submission
  const handleFormSubmit = (data: T) => {
    const validationErrors = validationRules
      .map((rule) => (!rule.validate(data) ? rule.message : null))
      .filter(Boolean) as string[];

    if (validationErrors.length > 0) {
      setErrors(validationErrors);
    } else {
      setErrors([]);
      onSubmit(data);
      hideModal();
    }
  };

  const updateDateValues = useCallback(() => {
    const dateFilter = filterConfig.find((item) => item.type == "date") as
      | DateFilter<T>
      | undefined;
    if (range == null || dateFilter == null) return;

    const timeValues = getTimeValues();

    if (range.from != null) {
      let dateStart = Date.parse(range.from.toJSON());

      // add time values
      if (timeValues.hoursStart != null) {
        dateStart += parseInt(timeValues.hoursStart) * 60 * 60 * 1000;
      }
      if (timeValues.minutesStart != null) {
        dateStart += parseInt(timeValues.minutesStart) * 60 * 1000;
      }

      let parsedStart: string | number | null = null;

      switch (dateFilter.returnValue) {
        case "string":
          parsedStart = new Date(dateStart).toISOString();
          break;
        case "number":
          parsedStart = dateStart / 1000;
          break;
      }

      if (parsedStart)
        setValue(dateFilter.nameStart, parsedStart as PathValue<T, Path<T>>);
    } else {
      resetField(dateFilter.nameStart);
    }

    if (range.to != null) {
      let dateEnd = Date.parse(range.to.toJSON());

      // add time values
      if (timeValues.hoursEnd != null) {
        dateEnd += parseInt(timeValues.hoursEnd) * 60 * 60 * 1000;
      }
      if (timeValues.minutesEnd != null) {
        dateEnd += parseInt(timeValues.minutesEnd) * 60 * 1000;
      }
      if (timeValues.hoursEnd == null && timeValues.minutesEnd == null) {
        dateEnd += 23 * 60 * 60 * 1000 + 59 * 60 * 1000;
      }
      let parsedEnd: string | number | null = null;

      switch (dateFilter.returnValue) {
        case "string":
          parsedEnd = new Date(dateEnd).toISOString();
          break;
        case "number":
          parsedEnd = dateEnd / 1000;
          break;
      }

      if (parsedEnd)
        setValue(dateFilter.nameEnd, parsedEnd as PathValue<T, Path<T>>);
    } else {
      resetField(dateFilter.nameEnd);
    }
  }, [range, getTimeValues, filterConfig, setValue, resetField]);

  useEffect(() => {
    const sub = timeWatch(() => {
      updateDateValues();
    });
    return () => sub.unsubscribe();
  }, [timeWatch, updateDateValues]);

  useEffect(() => {
    updateDateValues();
  }, [range]);

  // Handle form reset
  const handleReset = () => {
    reset();
    setRange(undefined); // Reset calendar range
    setErrors([]);
    onSubmit({} as T);
    hideModal();
  };

  return (
    <Modal
      className={styles.modal}
      isOpen={isModalOpen}
      onClose={hideModal}
      title="Фильтрация по поиску"
    >
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        {filterConfig.map((item, index) => {
          switch (item.type) {
            case "sum":
              return (
                <div key={index} className="flex flex-col gap-[8px] mb-[10px]">
                  <span className="text-[14px] font-semibold">
                    По диапозону суммы
                  </span>
                  <div className="flex gap-[8px]">
                    <Input
                      type="number"
                      name={item.minName}
                      control={control}
                      placeholder="5 000"
                      label="Минимальная"
                    />
                    <Input
                      type="number"
                      name={item.maxName}
                      control={control}
                      label="Максимальная"
                      placeholder="20 000"
                    />
                  </div>
                </div>
              );
            case "select":
              return (
                <div key={index} className="flex flex-col gap-[8px] mb-[10px]">
                  <span className="text-[14px] font-semibold">
                    {item.label}
                  </span>
                  <CustomSelect
                    name={item.name}
                    control={control}
                    options={item.options}
                    placeholder={item.placeholder || "Выберите значение"}
                  />
                </div>
              );
            case "date":
              return (
                <div key={index}>
                  {item.withTime && (
                    // <div>
                    <Grid
                      style={{ alignItems: "flex-end", marginBottom: "5px" }}
                    >
                      <GridCol span={3}>
                        <CustomSelect
                          label="Начало"
                          options={TimeHourOptions}
                          control={timeControl}
                          name="hoursStart"
                          placeholder="Часы"
                          classNames={{trigger: styles.timePadding}}
                        />
                      </GridCol>
                      <GridCol span={3}>
                        <CustomSelect
                          label=" "
                          options={TimeMinutesOptions}
                          control={timeControl}
                          name="minutesStart"
                          placeholder="Мин"
                          classNames={{trigger: styles.timePadding}}
                        />
                      </GridCol>
                      <GridCol span={3}>
                        <CustomSelect
                          label="Конец"
                          options={TimeHourOptions}
                          control={timeControl}
                          name="hoursEnd"
                          placeholder="Часы"
                          classNames={{trigger: styles.timePadding}}
                        />
                      </GridCol>
                      <GridCol span={3}>
                        <CustomSelect
                          label=" "
                          options={TimeMinutesOptions}
                          control={timeControl}
                          name="minutesEnd"
                          placeholder="Мин"
                          classNames={{trigger: styles.timePadding}}
                        />
                      </GridCol>
                    </Grid>
                  )}
                  <div className="flex justify-center">
                    <Calendar range={range} setRange={setRange} max={90} />
                  </div>
                </div>
              );
            default:
              return null;
          }
        })}

        {errors.length > 0 && (
          <div className={styles.errorNote}>
            {errors.map((error, index) => (
              <p key={index}>{error}</p>
            ))}
          </div>
        )}

        <div className="flex gap-[8px]">
          <Button
            className={styles.button}
            type="outline"
            onClick={handleReset}
            buttonType="button"
          >
            Сбросить фильтры
          </Button>
          <Button className={styles.button}>Применить фильтры</Button>
        </div>
      </form>
    </Modal>
  );
};

const TimeHourOptions = [
  { text: "00:00", value: "0" },
  { text: "01:00", value: "1" },
  { text: "02:00", value: "2" },
  { text: "03:00", value: "3" },
  { text: "04:00", value: "4" },
  { text: "05:00", value: "5" },
  { text: "06:00", value: "6" },
  { text: "07:00", value: "7" },
  { text: "08:00", value: "8" },
  { text: "09:00", value: "9" },
  { text: "10:00", value: "10" },
  { text: "11:00", value: "11" },
  { text: "12:00", value: "12" },
  { text: "13:00", value: "13" },
  { text: "14:00", value: "14" },
  { text: "15:00", value: "15" },
  { text: "16:00", value: "16" },
  { text: "17:00", value: "17" },
  { text: "18:00", value: "18" },
  { text: "19:00", value: "19" },
  { text: "20:00", value: "20" },
  { text: "21:00", value: "21" },
  { text: "22:00", value: "22" },
  { text: "23:00", value: "23" },
];

const TimeMinutesOptions = [
  { text: "00 мин", value: "0" },
  { text: "10 мин", value: "10" },
  { text: "20 мин", value: "20" },
  { text: "30 мин", value: "30" },
  { text: "40 мин", value: "40" },
  { text: "50 мин", value: "50" },
];
